import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { attendanceService } from '../../api/attendanceService';
import { useCategories } from '../../hooks/useCategories';
import { extractErrorMessage } from '../../utils/errorHandler';
import { excludeSuperAdminUsers } from '../../utils/reportFilters';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import CountyFilterSelect, { countyFilterToId, regionFilterToId } from '../../components/reports/CountyFilterSelect';
// import * as XLSX from 'xlsx';
import * as XLSX from 'xlsx-js-style';
import {
  Calendar,
  Download,
  RefreshCw,
  Filter,
  ChevronLeft,
  ChevronRight,
  FileText,
  Users,
  ChevronDown,
  FileSpreadsheet,
  Search
} from 'lucide-react';

const AttendanceView = () => {
  const navigate = useNavigate();
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Date range - current month by default
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedCounty, setSelectedCounty] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const { data: categoriesRaw = [], isLoading: loadingCategories } = useCategories();
  const categories = useMemo(() => {
    const filteredCategories = categoriesRaw.filter((cat) => {
      const categoryId = cat.categoryId || cat.id;
      const categoryName = cat.categoryName || cat.name || '';
      return (
        categoryId !== null &&
        categoryId !== undefined &&
        categoryName !== 'Select a category...' &&
        categoryName.trim() !== ''
      );
    });
    return Array.isArray(filteredCategories) ? filteredCategories : [];
  }, [categoriesRaw]);
  const [exporting, setExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef(null);
  
  // Custom date range filter
  const [useCustomDateRange, setUseCustomDateRange] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  
  // Refs for date inputs to enable programmatic focus
  const fromDateInputRef = useRef(null);
  const toDateInputRef = useRef(null);

  // Get current month date range (capped to today if viewing current month)
  const getMonthDateRange = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const startDate = new Date(year, month, 1);
    let endDate = new Date(year, month + 1, 0); // Last day of the month

    // If viewing current month, cap endDate to today (include today)
    const today = new Date();
    const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const isCurrentMonth = year === todayLocal.getFullYear() && month === todayLocal.getMonth();
    if (isCurrentMonth && endDate > todayLocal) {
      endDate = todayLocal; // Include today
    }

    // Format dates as YYYY-MM-DD (local timezone, not UTC)
    const formatDate = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    return {
      startDate: formatDate(startDate),
      endDate: formatDate(endDate)
    };
  };

  // Get tenant ID from storage
  const getTenantId = () => {
    return sessionStorage.getItem('tenantId') || 1;
  };

  // Debounce search for server-side filter
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setCurrentPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load attendance data (paginated)
  useEffect(() => {
    loadAttendanceData();
  }, [currentMonth, selectedCategory, selectedRegion, selectedCounty, fromDate, toDate, useCustomDateRange, currentPage, debouncedSearch]);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setExportMenuOpen(false);
      }
    };

    if (exportMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [exportMenuOpen]);

  const buildAttendanceReportParams = ({ exportAll = false, page = currentPage } = {}) => {
    let startDate;
    let endDate;
    if (useCustomDateRange && fromDate && toDate) {
      startDate = fromDate;
      endDate = toDate;
    } else {
      const monthRange = getMonthDateRange(currentMonth);
      startDate = monthRange.startDate;
      endDate = monthRange.endDate;
    }

    const params = {
      startDate,
      endDate,
      tenantId: parseInt(getTenantId(), 10),
    };

    if (selectedCategory !== 'all') {
      params.categoryId = parseInt(selectedCategory, 10);
    }
    const regionId = regionFilterToId(selectedRegion);
    if (regionId !== null) params.regionId = regionId;
    const countyId = countyFilterToId(selectedCounty);
    if (countyId !== null) params.countyId = countyId;
    if (debouncedSearch) params.search = debouncedSearch;

    if (exportAll) {
      params.exportAll = true;
    } else {
      params.page = page;
      params.size = pageSize;
    }
    return params;
  };

  const parseAttendanceReportResponse = (response) => {
    const payload = response?.data?.data ?? response?.data ?? response;
    if (Array.isArray(payload)) {
      return {
        content: excludeSuperAdminUsers(payload),
        totalElements: payload.length,
        totalPages: 1,
        pageNumber: 0,
      };
    }
    const content = Array.isArray(payload?.content) ? payload.content : [];
    return {
      content: excludeSuperAdminUsers(content),
      totalElements: Number(payload?.totalElements ?? content.length),
      totalPages: Number(payload?.totalPages ?? 1),
      pageNumber: Number(payload?.pageNumber ?? 0),
    };
  };

  const loadAttendanceData = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await attendanceService.getAttendanceReport(buildAttendanceReportParams());
      const parsed = parseAttendanceReportResponse(response);
      setAttendanceData(parsed.content);
      setTotalElements(parsed.totalElements);
      setTotalPages(Math.max(parsed.totalPages, 0));
    } catch (error) {
      console.error('Error loading attendance data:', error);
      setError(extractErrorMessage(error, 'Failed to load attendance data'));
      toast.error(extractErrorMessage(error, 'Failed to load attendance data'));
      setAttendanceData([]);
      setTotalElements(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  // Table rows come from the current API page (search is server-side)
  const filteredAttendanceData = attendanceData;

  const handlePreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    setUseCustomDateRange(false);
    setFromDate('');
    setToDate('');
    setCurrentPage(0);
  };

  const handleNextMonth = () => {
    const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Don't allow navigation to future months
    if (nextMonth <= today) {
      setCurrentMonth(nextMonth);
      setUseCustomDateRange(false);
      setFromDate('');
      setToDate('');
      setCurrentPage(0);
    } else {
      toast.error('Cannot navigate to future months');
    }
  };

  const handleCurrentMonth = () => {
    setCurrentMonth(new Date());
    setUseCustomDateRange(false);
    setFromDate('');
    setToDate('');
    setCurrentPage(0);
  };

  const handleFromDateChange = (e) => {
    const selectedDate = e.target.value;
    const today = new Date().toISOString().split('T')[0];
    
    // Validate: don't allow future dates
    if (selectedDate > today) {
      toast.error('From Date cannot be in the future');
      return;
    }
    
    // Validate: fromDate should not be after toDate
    if (toDate && selectedDate > toDate) {
      toast.error('From Date cannot be after To Date');
      return;
    }
    
    setFromDate(selectedDate);
    setUseCustomDateRange(true);
    setCurrentPage(0);
  };

  const handleToDateChange = (e) => {
    const selectedDate = e.target.value;
    const today = new Date().toISOString().split('T')[0];
    
    // Validate: don't allow future dates
    if (selectedDate > today) {
      toast.error('To Date cannot be in the future');
      return;
    }
    
    // Validate: toDate should not be before fromDate
    if (fromDate && selectedDate < fromDate) {
      toast.error('To Date cannot be before From Date');
      return;
    }
    
    setToDate(selectedDate);
    setUseCustomDateRange(true);
    setCurrentPage(0);
  };

  const handleExportPDF = async () => {
    setExportMenuOpen(false);
    try {
      setExporting(true);
      // Usual PDF API — full dataset (same filters; no list pagination)
      const params = buildAttendanceReportParams({ exportAll: true });
      const blob = await attendanceService.downloadAttendanceReportPDF(params);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Attendance_Report_${params.startDate}_to_${params.endDate}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error(extractErrorMessage(error, 'Failed to export PDF'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setExportMenuOpen(false);
    try {
      setExporting(true);

      // Usual report API with exportAll — full matching rows; Excel format unchanged
      const params = buildAttendanceReportParams({ exportAll: true });
      const response = await attendanceService.getAttendanceReport(params);
      const parsed = parseAttendanceReportResponse(response);
      const exportRows = parsed.content;

      if (exportRows.length === 0) {
        toast.error('No data to export');
        return;
      }

      const startDate = params.startDate;
      const endDate = params.endDate;

      // Prepare Excel data with full date format (DD-MM-YYYY) in headers
      const formatDateForExcel = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      };

      const headers = ['Employee Name', 'Email', ...monthDates.map(date => formatDateForExcel(date)), 'Present', 'Absent', 'Leave', 'Holiday', 'Total Days'];

      const rows = exportRows.map((employee) => {
        const attendanceMap = employee.attendanceMap || {};
        const row = [
          employee.empName || employee.fullName || 'N/A',
          employee.email || 'N/A',
          ...monthDates.map(date => {
            const status = attendanceMap[date] || 'A';
            return status;
          }),
          employee.presentCount || 0,
          employee.absentCount || 0,
          employee.leaveCount || 0,
          employee.holidayCount || 0,
          employee.totalDays || monthDates.length
        ];
        return row;
      });

      // Calculate summary totals
      const summaryRow = [
        'TOTAL',
        '',
        ...monthDates.map(() => ''),
        exportRows.reduce((sum, emp) => sum + (emp.presentCount || 0), 0),
        exportRows.reduce((sum, emp) => sum + (emp.absentCount || 0), 0),
        exportRows.reduce((sum, emp) => sum + (emp.leaveCount || 0), 0),
        exportRows.reduce((sum, emp) => sum + (emp.holidayCount || 0), 0),
        exportRows.reduce((sum, emp) => sum + (emp.totalDays || monthDates.length), 0)
      ];

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();


      const title = 'Attendance Report';
      const subtitle = `Attendance Report (${startDate} to ${endDate})`;

      // total number of columns
      const totalColumns = headers.length;

      // build sheet data with empty rows
      const sheetData = [
        [title],
        [subtitle],
        [],              // empty row
        headers,         // column headers
        ...rows,
        summaryRow
      ];
      const TITLE_ROW_INDEX = 0;       // Row 1
      const SUBTITLE_ROW_INDEX = 1;    // Row 2
      const HEADER_ROW_INDEX = 3;      // Row 4
      const DATA_START_ROW_INDEX = 4;  // Row 5 (FIRST DATA ROW)

      const ws = XLSX.utils.aoa_to_sheet(sheetData);

      ws['!merges'] = [
        {
          s: { r: 0, c: 0 },
          e: { r: 0, c: totalColumns - 1 }
        },
        {
          s: { r: 1, c: 0 },
          e: { r: 1, c: totalColumns - 1 }
        }
      ];

      // Title (Row 1)
      ws['A1'].s = {
        font: { bold: true, sz: 16 },
        alignment: { horizontal: 'center', vertical: 'center' }
      };

      // Subtitle (Row 2)
      ws['A2'].s = {
        font: { bold: true, sz: 12 },
        alignment: { horizontal: 'center', vertical: 'center' }
      };



      // Set column widths for better readability
      const colWidths = [
        { wch: 25 }, // Employee Name
        { wch: 30 }, // Email
        ...monthDates.map(() => ({ wch: 13 })), // Date columns
        { wch: 10 }, // Present
        { wch: 10 }, // Absent
        { wch: 10 }, // Leave
        { wch: 10 }, // Holiday
        { wch: 10 }  // Total Days
      ];
      ws['!cols'] = colWidths;

      const lastColIndex = headers.length - 1;

      // Apply autofilter ONLY on header row
      ws['!autofilter'] = {
        ref: XLSX.utils.encode_range({
          s: { r: HEADER_ROW_INDEX, c: 0 },          // A4 (Employee Name)
          e: { r: HEADER_ROW_INDEX, c: lastColIndex } // Last header column
        })
      };
      

      // Freeze panes: Freeze first row (headers) and first column (Employee Name)
      ws['!freeze'] = {
        xSplit: 1,  // Freeze first column
        ySplit: 1,  // Freeze first row
        topLeftCell: 'B5',  // Top-left visible cell after freeze
        activePane: 'bottomRight',
        state: 'frozen'
      };
      // Alternative method using views
      ws['!views'] = [{
        state: 'frozen',
        xSplit: 1,
        ySplit: 1,
        topLeftCell: 'B5',
        activeCell: 'B2'
      }];

      // Add conditional formatting for status cells (P/A/L/H colors)
      // Note: xlsx library has limited conditional formatting support
      // We'll add cell styles directly for status columns
      const statusColorMap = {
        'P': { fill: { fgColor: { rgb: 'C6EFCE' } }, font: { color: { rgb: '006100' }, bold: true } }, // Light green
        'A': { fill: { fgColor: { rgb: 'FFC7CE' } }, font: { color: { rgb: '9C0006' }, bold: true } }, // Light red
        'L': { fill: { fgColor: { rgb: 'FFEB9C' } }, font: { color: { rgb: '9C6500' }, bold: true } }, // Light yellow
        'H': { fill: { fgColor: { rgb: 'BDD7EE' } }, font: { color: { rgb: '002060' }, bold: true } }  // Light blue
      };

      // Apply colors and center alignment to status cells (date columns)
      const dateStartCol = 2; // Column C (0-indexed: A=0, B=1, C=2)
      rows.forEach((row, rowIndex) => {
        monthDates.forEach((date, dateIndex) => {
          const colIndex = dateStartCol + dateIndex;
          const cellAddress = XLSX.utils.encode_cell({
            r: DATA_START_ROW_INDEX + rowIndex,
            c: colIndex
          });
          const status = row[dateIndex + 2]; // +2 for Employee Name and Email columns

          if (status && statusColorMap[status.toUpperCase()]) {
            if (!ws[cellAddress]) {
              ws[cellAddress] = { t: 's', v: status };
            }
            // Apply color and center alignment
            ws[cellAddress].s = {
              ...statusColorMap[status.toUpperCase()],
              alignment: { horizontal: 'center', vertical: 'center' }
            };
          }
        });
      });

      // Center-align all data cells (employee name, email, and summary columns)
      const totalRows = rows.length;
      headers.forEach((header, colIndex) => {
        // Skip header row (already styled) and summary row (will be styled separately)
        for (let rowIndex = 0; rowIndex < totalRows; rowIndex++) {
          const cellAddress = XLSX.utils.encode_cell({
            r: DATA_START_ROW_INDEX + rowIndex,
            c: colIndex
          });
          if (ws[cellAddress]) {
            // If cell doesn't have style, create it
            if (!ws[cellAddress].s) {
              ws[cellAddress].s = {};
            }
            // Ensure center alignment
            if (!ws[cellAddress].s.alignment) {
              ws[cellAddress].s.alignment = {};
            }
            ws[cellAddress].s.alignment.horizontal = 'center';
            ws[cellAddress].s.alignment.vertical = 'center';
          }
        }
      });

      // Style header row - Bold and center-aligned
      // Style header row - Bold only (no background color)
      headers.forEach((header, colIndex) => {
        const cellAddress = XLSX.utils.encode_cell({ r: HEADER_ROW_INDEX, c: colIndex });

        if (!ws[cellAddress]) {
          ws[cellAddress] = { t: 's', v: header };
        }
        ws[cellAddress].s = {
          font: { bold: true }, // ✅ ONLY BOLD
          alignment: {
            horizontal: 'center',
            vertical: 'center',
            wrapText: true
          }
        };
      });


      // Style summary row (last row) - Bold and center-aligned
      const summaryRowIndex = DATA_START_ROW_INDEX + rows.length;
      headers.forEach((header, colIndex) => {
        const cellAddress = XLSX.utils.encode_cell({ r: summaryRowIndex, c: colIndex });
        if (ws[cellAddress]) {
          ws[cellAddress].s = {
            font: { bold: true, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '70AD47' } }, // Green background
            alignment: { horizontal: 'center', vertical: 'center' }
          };
        }
      });

      // Add Excel Table (ListObject) - This creates a formatted table with built-in filters
      // Note: xlsx library doesn't directly support Excel Tables, but autofilter provides similar functionality
      // The autofilter we added above will provide the filtering capability

      // 🔒 FINAL SAFETY PASS – force center alignment everywhere
      const range = XLSX.utils.decode_range(ws['!ref']);

      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          if (!ws[cellAddress]) continue;

          ws[cellAddress].s = ws[cellAddress].s || {};
          ws[cellAddress].s.alignment = {
            horizontal: 'center',
            vertical: 'center',
            wrapText: true
          };
        }
      }


      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Attendance Report');

      // Generate Excel file
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Attendance_Report_${startDate}_to_${endDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Excel file downloaded successfully with filters and formatting');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast.error(extractErrorMessage(error, 'Failed to export Excel'));
    } finally {
      setExporting(false);
    }
  };

  // Generate all dates in the range (custom date range or month range)
  const monthDates = useMemo(() => {
    let startDate, endDate;
    
    // Use custom date range if enabled, otherwise use month range
    if (useCustomDateRange && fromDate && toDate) {
      startDate = fromDate;
      endDate = toDate;
    } else {
      const monthRange = getMonthDateRange(currentMonth);
      startDate = monthRange.startDate;
      endDate = monthRange.endDate;
    }
    
    const dates = [];

    // Parse dates as local dates (not UTC) to avoid timezone issues
    const startParts = startDate.split('-');
    const endParts = endDate.split('-');
    const start = new Date(parseInt(startParts[0]), parseInt(startParts[1]) - 1, parseInt(startParts[2]));
    const end = new Date(parseInt(endParts[0]), parseInt(endParts[1]) - 1, parseInt(endParts[2]));

    // Get today's date in local timezone (reset time to 00:00:00)
    const today = new Date();
    const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    // Only include dates up to and including today
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      // Create date object with only date part (no time)
      const currentDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());

      // Include dates that are today or in the past (use <= to include today)
      // Compare only the date part, ignoring time
      if (currentDate.getTime() <= todayLocal.getTime()) {
        // Format as YYYY-MM-DD for consistency
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        dates.push(`${year}-${month}-${day}`);
      }
    }

    return dates;
  }, [currentMonth, useCustomDateRange, fromDate, toDate]);

  // Format date for display (day number only) - kept for backward compatibility
  const formatDay = (dateString) => {
    const date = new Date(dateString);
    return date.getDate().toString().padStart(2, '0');
  };

  // Format date for display with full date (DD-MM-YYYY)
  const formatDateFull = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'P':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'A':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'L':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'H':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  // Get status label
  const getStatusLabel = (status) => {
    switch (status?.toUpperCase()) {
      case 'P':
        return 'Present';
      case 'A':
        return 'Absent';
      case 'L':
        return 'Leave';
      case 'H':
        return 'Holiday';
      default:
        return status || '-';
    }
  };

  // Format month display
  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Format date range for display
  const formatDateRange = () => {
    if (useCustomDateRange && fromDate && toDate) {
      const formatDisplayDate = (dateStr) => {
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      };
      return `${formatDisplayDate(fromDate)} to ${formatDisplayDate(toDate)}`;
    }
    return formatMonthYear(currentMonth);
  };

  // Check if the month is in the future
  const isFutureMonth = (date) => {
    const today = new Date();
    const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
    const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    return monthStart > currentMonthStart;
  };

  return (
    <div className="space-y-6 p-6">
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-4">
            {/* Month Navigation */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreviousMonth}
                disabled={loading}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-md border">
                <Calendar className="h-4 w-4 text-gray-600" />
                <span className="font-medium text-gray-900 min-w-[150px] text-center">
                  {formatMonthYear(currentMonth)}
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextMonth}
                disabled={loading || isFutureMonth(currentMonth)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCurrentMonth}
                disabled={loading}
              >
                Current Month
              </Button>
            </div>

            {/* Category Filter */}
            <div className="flex items-center gap-2 shrink-0">
              <Select
                value={selectedCategory}
                onValueChange={(value) => {
                  setSelectedCategory(value);
                  setCurrentPage(0);
                }}
                disabled={loadingCategories || loading}
              >
                <SelectTrigger className="w-[200px] h-9 rounded-xl border border-primary bg-white text-[#000000] hover:bg-[#E0F8F3] font-medium text-sm focus:outline-none focus:!ring-1 focus:!ring-primary/30 focus:!border-primary">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.categoryId || category.id} value={String(category.categoryId || category.id)}>
                      {category.categoryName || category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Region / County Filter */}
            <div className="flex items-center gap-2 shrink-0">
              <CountyFilterSelect
                variant="button"
                regionValue={selectedRegion}
                countyValue={selectedCounty}
                onRegionChange={(value) => {
                  setSelectedRegion(value);
                  setCurrentPage(0);
                }}
                onCountyChange={(value) => {
                  setSelectedCounty(value);
                  setCurrentPage(0);
                }}
                disabled={loading}
              />
            </div>

            {/* Employee search */}
            <div className="flex items-center gap-2 flex-1 min-w-[220px] max-w-sm">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Search:</label>
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <Input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Name or Email"
                  disabled={loading}
                  className="pl-9"
                />
              </div>
            </div>
          </div>

          {/* Custom Date Range Filter + Export / Refresh */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <style>{`
              /* Hide default browser calendar picker indicator for date inputs in this component */
              .attendance-date-input::-webkit-calendar-picker-indicator {
                display: none !important;
                -webkit-appearance: none !important;
                opacity: 0 !important;
                width: 0 !important;
                height: 0 !important;
              }
              .attendance-date-input::-webkit-inner-spin-button,
              .attendance-date-input::-webkit-clear-button {
                display: none !important;
                -webkit-appearance: none !important;
              }
            `}</style>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">From Date:</label>
                <div className="relative">
                  <Calendar 
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 cursor-pointer hover:text-gray-600 z-10" 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const input = fromDateInputRef.current;
                      // Ensure we have a valid input element with focus method
                      if (input && typeof input.focus === 'function') {
                        input.focus();
                        // Try to show the native date picker if available (modern browsers)
                        if (input.showPicker && typeof input.showPicker === 'function') {
                          try {
                            const pickerPromise = input.showPicker();
                            // Only call catch if it returns a Promise
                            if (pickerPromise && typeof pickerPromise === 'object' && typeof pickerPromise.catch === 'function') {
                              pickerPromise.catch(() => {
                                // Fallback to focus if showPicker fails
                                if (input && typeof input.focus === 'function') {
                                  input.focus();
                                }
                              });
                            }
                          } catch (error) {
                            // If showPicker throws an error, just focus
                            if (input && typeof input.focus === 'function') {
                              input.focus();
                            }
                          }
                        }
                      }
                    }}
                  />
                  <Input
                    ref={fromDateInputRef}
                    type="date"
                    value={fromDate}
                    max={new Date().toISOString().split('T')[0]}
                    onChange={handleFromDateChange}
                    disabled={loading}
                    className="attendance-date-input pl-10 pr-3 w-[180px]"
                    style={{
                      WebkitAppearance: 'none',
                      MozAppearance: 'textfield'
                    }}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">To Date:</label>
                <div className="relative">
                  <Calendar 
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 cursor-pointer hover:text-gray-600 z-10" 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const input = toDateInputRef.current;
                      // Ensure we have a valid input element with focus method
                      if (input && typeof input.focus === 'function') {
                        input.focus();
                        // Try to show the native date picker if available (modern browsers)
                        if (input.showPicker && typeof input.showPicker === 'function') {
                          try {
                            const pickerPromise = input.showPicker();
                            // Only call catch if it returns a Promise
                            if (pickerPromise && typeof pickerPromise === 'object' && typeof pickerPromise.catch === 'function') {
                              pickerPromise.catch(() => {
                                // Fallback to focus if showPicker fails
                                if (input && typeof input.focus === 'function') {
                                  input.focus();
                                }
                              });
                            }
                          } catch (error) {
                            // If showPicker throws an error, just focus
                            if (input && typeof input.focus === 'function') {
                              input.focus();
                            }
                          }
                        }
                      }
                    }}
                  />
                  <Input
                    ref={toDateInputRef}
                    type="date"
                    value={toDate}
                    min={fromDate || undefined}
                    max={new Date().toISOString().split('T')[0]}
                    onChange={handleToDateChange}
                    disabled={loading}
                    className="attendance-date-input pl-10 pr-3 w-[180px]"
                    style={{
                      WebkitAppearance: 'none',
                      MozAppearance: 'textfield'
                    }}
                  />
                </div>
              </div>

              {(fromDate || toDate) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setFromDate('');
                    setToDate('');
                    setUseCustomDateRange(false);
                    setCurrentPage(0);
                  }}
                  disabled={loading}
                  className="text-red-600 hover:text-red-700"
                >
                  Clear Date Range
                </Button>
              )}

              <div className="flex items-center gap-2 ml-auto shrink-0">
                <div className="relative" ref={exportMenuRef}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setExportMenuOpen((v) => !v)}
                    disabled={exporting || loading}
                    className="flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    {exporting ? 'Exporting...' : 'Export'}
                    <ChevronDown className="h-4 w-4 ml-1" />
                  </Button>
                  {exportMenuOpen && (
                    <div className="absolute right-0 top-full mt-1 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-10 min-w-[160px]">
                      <button
                        type="button"
                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                        onClick={handleExportPDF}
                        disabled={exporting || loading}
                      >
                        <FileText className="h-4 w-4" />
                        Export PDF
                      </button>
                      <button
                        type="button"
                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                        onClick={handleExportExcel}
                        disabled={exporting || loading}
                      >
                        <FileSpreadsheet className="h-4 w-4" />
                        Export Excel
                      </button>
                    </div>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadAttendanceData}
                  disabled={loading}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Message */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-red-800">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Attendance Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Attendance Report - {formatDateRange()}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
              <span className="ml-3 text-gray-600">Loading attendance data...</span>
            </div>
          ) : filteredAttendanceData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <FileText className="h-12 w-12 mb-4 text-gray-400" />
              <p className="text-lg font-medium">
                {totalElements === 0 && !debouncedSearch ? 'No attendance data found' : 'No employees match your search'}
              </p>
              <p className="text-sm mt-1">
                {totalElements === 0 && !debouncedSearch
                  ? 'Try selecting a different month or category'
                  : 'Try a different name, email, or username'}
              </p>
            </div>
          ) : (
            <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b-2 border-gray-200">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 sticky left-0 bg-gray-50 z-10 border-r border-gray-200 min-w-[200px]">
                      Employee Name
                    </th>
                    {monthDates.map((date) => (
                      <th
                        key={date}
                        className="px-2 py-3 text-center text-xs font-semibold text-gray-700 border-r border-gray-200 min-w-[100px]"
                        title={new Date(date).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      >
                        {formatDateFull(date)}
                      </th>
                    ))}
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 bg-gray-100">
                      Summary
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAttendanceData.map((employee, idx) => {
                    const attendanceMap = employee.attendanceMap || {};
                    return (
                      <tr
                        key={employee.userId || idx}
                        className="border-b border-gray-200 hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 sticky left-0 bg-white z-10 border-r border-gray-200">
                          <div className="flex flex-col">
                            <span>{employee.empName || employee.fullName || 'N/A'}</span>
                            {employee.email && (
                              <span className="text-xs text-gray-500">{employee.email}</span>
                            )}
                          </div>
                        </td>
                        {monthDates.map((date) => {
                          // Get status from attendance map, default to 'A' (Absent)
                          // The date key should match exactly as YYYY-MM-DD format
                          const status = attendanceMap[date] || 'A';
                          return (
                            <td
                              key={date}
                              className="px-2 py-3 text-center border-r border-gray-200"
                            >
                              <Badge
                                className={`${getStatusColor(status)} text-xs font-medium px-1.5 py-0.5`}
                                title={getStatusLabel(status)}
                              >
                                {status}
                              </Badge>
                            </td>
                          );
                        })}
                        <td className="px-4 py-3 text-center text-sm bg-gray-50">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center justify-center gap-2">
                              <span className="text-xs text-gray-600">P:</span>
                              <span className="font-semibold text-green-700">
                                {employee.presentCount || 0}
                              </span>
                            </div>
                            <div className="flex items-center justify-center gap-2">
                              <span className="text-xs text-gray-600">A:</span>
                              <span className="font-semibold text-red-700">
                                {employee.absentCount || 0}
                              </span>
                            </div>
                            {employee.leaveCount > 0 && (
                              <div className="flex items-center justify-center gap-2">
                                <span className="text-xs text-gray-600">L:</span>
                                <span className="font-semibold text-yellow-700">
                                  {employee.leaveCount || 0}
                                </span>
                              </div>
                            )}
                            {employee.holidayCount > 0 && (
                              <div className="flex items-center justify-center gap-2">
                                <span className="text-xs text-gray-600">H:</span>
                                <span className="font-semibold text-blue-700">
                                  {employee.holidayCount || 0}
                                </span>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between mt-4 pt-4 border-t border-gray-200 gap-3">
                <div className="text-sm text-muted-foreground">
                  Showing {currentPage * pageSize + 1} to{' '}
                  {Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} employees
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                    disabled={currentPage === 0 || loading}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <span className="text-sm text-gray-600 px-2">
                    Page {currentPage + 1} of {Math.max(totalPages, 1)}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={currentPage >= totalPages - 1 || loading}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-sm font-medium text-gray-700">Legend:</span>
            <Badge className="bg-green-100 text-green-800 border-green-300">P - Present</Badge>
            <Badge className="bg-red-100 text-red-800 border-red-300">A - Absent</Badge>
            <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">L - Leave</Badge>
            <Badge className="bg-blue-100 text-blue-800 border-blue-300">H - Holiday</Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AttendanceView;
